// OIDC Authorization Code + PKCE flow for Keycloak

import { KC_BASE_URL } from './runtimeEnv.js';

const KC_BASE   = `${KC_BASE_URL}/realms/parsina`;
const CLIENT_ID = process.env.PARSINA_KC_CLIENT_ID || 'parsina-web';

// Always derived at call time so redirect_uri is consistent across
// the auth request and token exchange even if the page reloads.
function getRedirectUri() {
  return `${window.location.origin}/`;
}

// ── PKCE helpers ─────────────────────────────────────────────────────────────

function randomBytes(len) {
  const arr = new Uint8Array(len);
  crypto.getRandomValues(arr);
  return arr;
}

function base64url(buf) {
  return btoa(String.fromCharCode(...buf))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function sha256(str) {
  const enc = new TextEncoder().encode(str);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  return new Uint8Array(buf);
}

async function generateChallenge(verifier) {
  return base64url(await sha256(verifier));
}

// ── Token storage ─────────────────────────────────────────────────────────────

// A sign-in lasts as long as Keycloak's refresh token — 30 days on beta1 — not
// as long as one tab (PM-0158). The REFRESH token and the id token (the logout
// hint) live in localStorage, so a new tab, a reopened browser or a Chrome that
// Android killed in the background signs in silently from them. The 5-minute
// ACCESS token stays per tab. Before this, all three lived in sessionStorage and
// every new tab or browser restart threw a valid 30-day token away and sent the
// user to the login form. A copy left in sessionStorage by an older build is
// still read, so a tab open across the deploy keeps working.
const PERSISTED = ['kc_refresh', 'kc_id_token'];
function durable() {
  try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; }
}
function readPersisted(key) {
  try {
    const v = durable()?.getItem(key);
    if (v) return v;
  } catch { /* storage blocked: fall through to the tab's copy */ }
  return sessionStorage.getItem(key);
}

export function getToken()   { return sessionStorage.getItem('kc_token'); }
export function getRefresh() { return readPersisted('kc_refresh'); }
export function getIdToken() { return readPersisted('kc_id_token'); }

function saveTokens(token, refresh, idToken) {
  sessionStorage.setItem('kc_token', token);
  const pairs = [['kc_refresh', refresh], ['kc_id_token', idToken]].filter(([, v]) => v);
  try {
    const store = durable();
    if (!store) throw new Error('no localStorage');
    pairs.forEach(([k, v]) => store.setItem(k, v));
    pairs.forEach(([k]) => sessionStorage.removeItem(k));
  } catch {
    // Private mode or blocked storage: the tab still works, as it always did.
    pairs.forEach(([k, v]) => sessionStorage.setItem(k, v));
  }
}

export function clearTokens() {
  ['kc_token', 'kc_refresh', 'kc_id_token', 'kc_verifier', 'kc_state'].forEach(k =>
    sessionStorage.removeItem(k)
  );
  try { PERSISTED.forEach(k => durable()?.removeItem(k)); } catch { /* nothing stored */ }
}

// Keycloak ROTATES the refresh token. With it shared across tabs, two tabs
// refreshing at the same moment would present the same token twice and the
// second would be refused — and a refused refresh signs the user out. The
// browser's Web Locks serialise refreshes across every tab of this origin; each
// refresh reads the stored token inside the lock, so it uses the one the
// previous tab just received. Where Web Locks are missing, the per-tab
// single-flight below still holds.
async function withRefreshLock(fn) {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : null;
  if (!locks || typeof locks.request !== 'function') return fn();
  return locks.request('parsina-kc-refresh', () => fn());
}

// ── Redirect-loop guard ───────────────────────────────────────────────────────
// Records each redirect timestamp. If 3+ happen within 30 s, stops redirecting
// and lets the app render an error screen instead.

const LOOP_KEY   = 'kc_redirect_ts';
const LOOP_LIMIT = 3;
const LOOP_WIN   = 30_000; // ms

function redirectAllowed() {
  const raw    = sessionStorage.getItem(LOOP_KEY);
  const hist   = raw ? JSON.parse(raw) : [];
  const now    = Date.now();
  const recent = hist.filter(ts => now - ts < LOOP_WIN);
  if (recent.length >= LOOP_LIMIT) return false;
  sessionStorage.setItem(LOOP_KEY, JSON.stringify([...recent, now]));
  return true;
}

export function clearRedirectGuard() {
  sessionStorage.removeItem(LOOP_KEY);
}

// ── Auth flow ─────────────────────────────────────────────────────────────────

export async function redirectToLogin() {
  if (!redirectAllowed()) {
    // Too many consecutive redirects — surface the error to the user instead.
    clearTokens();
    window.history.replaceState({}, '', '/?auth_error=loop');
    window.location.reload();
    return;
  }

  const verifier  = base64url(randomBytes(32));
  const challenge = await generateChallenge(verifier);
  const state     = base64url(randomBytes(16));

  sessionStorage.setItem('kc_verifier', verifier);
  sessionStorage.setItem('kc_state',    state);

  const params = new URLSearchParams({
    response_type:         'code',
    client_id:             CLIENT_ID,
    redirect_uri:          getRedirectUri(),
    scope:                 'openid profile email',
    state,
    code_challenge:        challenge,
    code_challenge_method: 'S256',
  });

  window.location.href = `${KC_BASE}/protocol/openid-connect/auth?${params}`;
}

// There is deliberately NO silent `reauthorize()` (prompt=none) here. The
// doctor client carries one to pick up client roles Keycloak assigns during
// onboarding; this app's authorization does not live in the token at all — the
// dental gate is a doctorpreference lookup the BFF makes per request and
// invalidates on `completeOnboardingStep1` — so re-minting the token after
// onboarding would change nothing, and an unused auth redirect is one someone
// eventually calls for the wrong reason.

export async function handleCallback() {
  const params   = new URLSearchParams(window.location.search);
  const error    = params.get('error');
  const code     = params.get('code');
  const state    = params.get('state');
  const verifier = sessionStorage.getItem('kc_verifier');
  const saved    = sessionStorage.getItem('kc_state');

  console.log('[handleCallback] error:', error, '| hasCode:', !!code, '| stateMatch:', state === saved, '| hasVerifier:', !!verifier);

  // Keycloak returned an explicit error (access_denied, login_required, etc.)
  if (error) {
    console.warn('[handleCallback] KC returned error:', error, params.get('error_description'));
    clearTokens();
    window.history.replaceState({}, '', '/');
    return false;
  }

  // Missing or mismatched PKCE / state — don't retry immediately
  if (!code || !verifier || state !== saved) {
    console.warn('[handleCallback] missing code/verifier or state mismatch — aborting');
    window.history.replaceState({}, '', '/');
    return false;
  }

  const res = await fetch(`${KC_BASE}/protocol/openid-connect/token`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type:    'authorization_code',
      client_id:     CLIENT_ID,
      redirect_uri:  getRedirectUri(),
      code,
      code_verifier: verifier,
    }),
  });

  window.history.replaceState({}, '', '/');

  if (!res.ok) {
    console.error('[handleCallback] token exchange failed:', res.status, await res.text());
    return false;
  }

  const data = await res.json();
  if (!data.access_token) {
    // The FIELDS, never the payload: a token-endpoint response is the one body
    // in this app that can carry a bearer credential, and the browser console
    // is readable by any script on the page and by every support screenshot.
    console.error(
      '[handleCallback] no access_token in response. keys:', Object.keys(data || {}).join(','),
      '| error:', data?.error, data?.error_description,
    );
    return false;
  }

  // Log roles from new token
  try {
    const claims = JSON.parse(atob(data.access_token.split('.')[1].replace(/-/g,'+').replace(/_/g,'/')));
    const roles = claims?.resource_access?.['parsina-bff']?.roles || [];
    console.log('[handleCallback] new token roles:', roles, '| sub:', claims?.sub, '| clinic_id:', claims?.clinic_id);
  } catch { /* non-fatal */ }

  saveTokens(data.access_token, data.refresh_token, data.id_token);
  clearRedirectGuard();
  console.log('[handleCallback] tokens saved successfully');
  return true;
}

// Concurrent refresh, deduplicated.
//
// Reads fire in parallel on every screen — useToday alone issues two
// (`Promise.allSettled`), and the layout adds `me` + `myClinics` on top. When
// the access token has expired they ALL reach getValidToken() at the same
// moment and each calls this with the SAME refresh token.
//
// That is a race with a session-ending outcome: Keycloak rotates refresh
// tokens, and with «Revoke Refresh Token» enabled the second presentation of
// one is `invalid_grant` — so the loser's `clearTokens()` below wipes the
// tokens the winner had just saved, and the next request finds no token and
// bounces to login. Even without revocation the two `saveTokens()` calls race
// and the older pair can land last.
//
// One in-flight promise shared by every caller removes the race in every realm
// configuration and costs one round trip instead of N. The exported signature
// is unchanged: still a Promise<boolean>.
let refreshInFlight = null;

function refreshAccessTokenInTab() {
  if (refreshInFlight) return refreshInFlight;
  // `.finally` passes the resolution value through, so callers still receive
  // the boolean; the slot is cleared either way so a later expiry can retry.
  refreshInFlight = performRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function performRefresh() {
  const refresh = getRefresh();
  if (!refresh) return false;

  const res = await fetch(`${KC_BASE}/protocol/openid-connect/token`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type:    'refresh_token',
      client_id:     CLIENT_ID,
      refresh_token: refresh,
    }),
  });

  if (!res.ok) { clearTokens(); return false; }
  const data = await res.json();
  if (!data.access_token) { clearTokens(); return false; }
  saveTokens(data.access_token, data.refresh_token, data.id_token);
  return true;
}

export function logout() {
  const idToken = getIdToken();
  clearTokens();
  clearRedirectGuard();
  const params = new URLSearchParams({
    client_id:                CLIENT_ID,
    post_logout_redirect_uri: getRedirectUri(),
  });
  if (idToken) params.set('id_token_hint', idToken);
  window.location.href = `${KC_BASE}/protocol/openid-connect/logout?${params}`;
}

export function parseJwt(token) {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

export function isTokenExpired(token) {
  const p = parseJwt(token);
  if (!p) return true;
  return Date.now() >= p.exp * 1000 - 30_000; // 30 s buffer
}

// The exported refresh: one per tab (single-flight above), one at a time across
// tabs (Web Lock), so a rotated refresh token is never presented twice (PM-0158).
export function refreshAccessToken() {
  return withRefreshLock(() => refreshAccessTokenInTab());
}
